import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../ThemeContext";
import { get, put } from "../api";
import { Card, EmptyState } from "../components/UI";
import T from "../i18n";
import { LANGUAGES } from "../i18n";

// IMPORTANTE: Field declarado FORA do componente pra não ser recriado
// a cada render (evita perda de foco no input ao digitar)
function Field({ label, value, onChange, ph, type = "text", iS, lS }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={lS}>{label}</label>
      <input type={type} placeholder={ph} value={value} onChange={e => onChange(e.target.value)} style={iS} />
    </div>
  );
}

export default function Configuracao({ sub, lojaId, lang, setLang }) {
  const { theme: C, config: themeCfg, updateConfig, PRESETS } = useTheme();
  const _ = (k) => T(lang, k);
  const [loja, setLoja] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  // Horários por dia da semana — chaves estáveis pra salvar no banco
  // O label traduzido é calculado em tempo de renderização via labelDia()
  // Cada dia tem turnos[] pra suportar múltiplos turnos (café/almoço/jantar etc).
  const diasKeys = ["seg", "ter", "qua", "qui", "sex", "sab", "dom"];
  const labelDia = (k) => ({
    seg: _("segunda"), ter: _("terca"), qua: _("quarta"),
    qui: _("quinta"), sex: _("sexta"), sab: _("sabado"), dom: _("domingo")
  }[k] || k);
  const [horarios, setHorarios] = useState(diasKeys.map(k => ({
    dia: k,
    aberto: true,
    turnos: [{ abre: "08:00", fecha: "23:00" }]
  })));

  const [fDados, setFDados] = useState({ nome: "", telefone: "", email: "", endereco: "", cidade: "", estado: "", cnpj: "", cupom: "", latitude: "", longitude: "" });
  const [fApi, setFApi] = useState({ mp_access_token: "", mp_public_key: "", webhook_url: "", usar_chave_propria: 0, token_configurado: 0 });
  const [fImp, setFImp] = useState({ tipo: "bluetooth", nome_dispositivo: "", largura_papel: "80", auto_imprimir: "1" });

  // ── PDV Desktop (Electron) — gerencia impressora térmica via window.pdvElectron ──
  const isPDVDesktop = typeof window !== "undefined" && !!window.pdvElectron;
  const [pdvImpressoras, setPdvImpressoras] = useState([]);
  const [pdvDestinoAtivo, setPdvDestinoAtivo] = useState("caixa"); // caixa | cozinha | bar
  const [pdvLoading, setPdvLoading] = useState(false);
  const [pdvTestando, setPdvTestando] = useState(false);

  // Estrutura nova: cada destino tem sua config independente
  const PRINTER_DEFAULT = {
    ativa: false, conexao: "printer", name: "", ip: "", port: 9100,
    type: "epson", width: 48, font: 0, auto_cut: true, vias: 1
  };

  const [pdvPrinters, setPdvPrinters] = useState({
    caixa:   { ...PRINTER_DEFAULT, ativa: true },
    cozinha: { ...PRINTER_DEFAULT },
    bar:     { ...PRINTER_DEFAULT }
  });
  const [pdvAutoPrint, setPdvAutoPrint] = useState(true);

  useEffect(() => {
    if (!isPDVDesktop) return;
    (async () => {
      try {
        const all = await window.pdvElectron.config.getAll();
        // Pega estrutura nova OU migra da antiga
        let printers = all.printers;
        if (!printers || !printers.caixa) {
          // Migração: cria do zero baseado no antigo
          printers = {
            caixa: {
              ativa: true,
              conexao: all.printer_connection || "printer",
              name: all.printer_name || "",
              ip: all.printer_ip || "",
              port: all.printer_port || 9100,
              type: all.printer_type || "epson",
              width: all.printer_width || 48,
              font: all.printer_font || 0,
              auto_cut: all.printer_auto_cut !== false,
              vias: 1
            },
            cozinha: { ...PRINTER_DEFAULT },
            bar: { ...PRINTER_DEFAULT }
          };
        }
        // Garante todos os destinos com defaults
        const completo = {
          caixa:   { ...PRINTER_DEFAULT, ...(printers.caixa || {}) },
          cozinha: { ...PRINTER_DEFAULT, ...(printers.cozinha || {}) },
          bar:     { ...PRINTER_DEFAULT, ...(printers.bar || {}) }
        };
        setPdvPrinters(completo);
        setPdvAutoPrint(all.printer_auto_print !== false);

        const lista = await window.pdvElectron.printer.listar();
        setPdvImpressoras(lista || []);
      } catch (e) { console.error("PDV config erro:", e); }
    })();
  }, [isPDVDesktop]);

  const pdvAtualizarLista = async () => {
    setPdvLoading(true);
    try {
      const lista = await window.pdvElectron.printer.listar();
      setPdvImpressoras(lista || []);
    } catch (e) {}
    setPdvLoading(false);
  };

  // Atualiza um campo de UM destino específico
  const setDestino = (destino, novosCampos) => {
    setPdvPrinters(prev => ({
      ...prev,
      [destino]: { ...prev[destino], ...novosCampos }
    }));
  };

  const pdvSalvar = async () => {
    if (!isPDVDesktop) return false;
    try {
      const e = window.pdvElectron;
      // Salva estrutura completa
      await e.config.set("printers", pdvPrinters);
      await e.config.set("printer_auto_print", !!pdvAutoPrint);
      return true;
    } catch (e) { return false; }
  };

  const pdvTestar = async (destino) => {
    const cfg = pdvPrinters[destino];
    if (cfg.conexao === "printer" && !cfg.name) { notify("Selecione uma impressora!", false); return; }
    if (cfg.conexao === "tcp" && !cfg.ip) { notify("Informe o IP!", false); return; }
    setPdvTestando(true);
    const ok = await pdvSalvar();
    if (!ok) { setPdvTestando(false); notify("Erro ao salvar config", false); return; }
    try {
      const r = await window.pdvElectron.printer.teste(destino);
      if (r.ok) notify(`Teste ${destino} enviado!`);
      else notify("Erro: " + (r.erro || "desconhecido"), false);
    } catch (e) { notify("Erro: " + e.message, false); }
    setPdvTestando(false);
  };

  // Prévia da impressão
  const gerarPreview = (destino) => {
    const cfg = pdvPrinters[destino];
    const w = parseInt(cfg.width) || 48;
    const linhaJ = (esq, dir) => {
      const livre = w - esq.length - dir.length;
      return esq + (livre > 0 ? " ".repeat(livre) : " ") + dir;
    };
    const center = (s) => {
      if (s.length >= w) return s;
      const pad = Math.floor((w - s.length) / 2);
      return " ".repeat(pad) + s;
    };
    const sep = (c) => c.repeat(w);
    let regua = "";
    for (let i = 1; i <= w; i++) regua += (i % 10 === 0) ? String(Math.floor(i / 10)) : ".";

    if (destino === "caixa") {
      return [
        center(loja?.nome || "MixUp PDV"),
        sep("="),
        "Pedido: #TESTE",
        `Data: ${new Date().toLocaleString("pt-BR")}`,
        "Cliente: João Silva",
        sep("-"),
        "ITENS:",
        linhaJ("1x Hamburguer Cheddar", "R$ 35,90"),
        linhaJ("2x Coca-Cola 350ml", "R$ 14,00"),
        sep("-"),
        linhaJ("TOTAL", "R$ 49,90"),
        sep("="),
        "Pagamento: PIX",
        "",
        regua
      ].join("\n");
    } else if (destino === "cozinha") {
      return [
        center(loja?.nome || "MixUp PDV"),
        sep("="),
        center("*** COMANDA COZINHA ***"),
        sep("="),
        "Cliente: João Silva",
        `Hora: ${new Date().toLocaleTimeString("pt-BR")}`,
        sep("="),
        "1x HAMBURGUER CHEDDAR",
        "   >> sem cebola",
        "1x BATATA FRITA GRANDE",
        sep("="),
        "",
        regua
      ].join("\n");
    } else {
      return [
        center(loja?.nome || "MixUp PDV"),
        sep("="),
        center("*** BAR / COPA ***"),
        sep("="),
        "Cliente: João Silva",
        `Hora: ${new Date().toLocaleTimeString("pt-BR")}`,
        sep("="),
        "2x COCA-COLA 350ML",
        "1x AGUA SEM GAS",
        sep("="),
        "",
        regua
      ].join("\n");
    }
  };
  const [pagOnlineAtivo, setPagOnlineAtivo] = useState(false);
  const [tiposPag, setTiposPag] = useState([
    { key: "pix", label: "PIX", ativo: true },
    { key: "credito", label: _("cartaoCredito"), ativo: true },
    { key: "debito", label: _("cartaoDebito"), ativo: true },
    { key: "dinheiro", label: _("dinheiro"), ativo: true },
    { key: "cashback", label: "Cashback", ativo: false },
    { key: "vale_refeicao", label: _("valeRefeicao"), ativo: false },
  ]);

  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 2800); };

  const fetchLoja = useCallback(async () => {
    try {
      const res = await get("lojas", { id: lojaId });
      if (res.dados && res.dados.length > 0) {
        const l = res.dados[0];
        setLoja(l);
        setFDados({ nome: l.nome || "", telefone: l.telefone || "", email: l.email || "", endereco: l.endereco || "", cidade: l.cidade || "", estado: l.estado || "", cnpj: l.cnpj || "", cupom: l.cupom || "", latitude: l.latitude || "", longitude: l.longitude || "" });

        // Carregar horários por dia da semana (suporta turnos[] novo e formato antigo)
        if (l.horarios_json) {
          try {
            const parsed = JSON.parse(l.horarios_json);
            if (Array.isArray(parsed) && parsed.length > 0) {
              // Reordenar conforme diasKeys pra garantir consistência (seg → dom)
              const ordenado = diasKeys.map(k => {
                const found = parsed.find(p => p.dia === k);
                if (!found) return { dia: k, aberto: true, turnos: [{ abre: "08:00", fecha: "23:00" }] };

                // Converte turnos múltiplos OU formato antigo (abre/fecha direto) → turnos[]
                let turnos;
                if (Array.isArray(found.turnos) && found.turnos.length > 0) {
                  turnos = found.turnos.map(t => ({
                    abre: t.abre || "08:00",
                    fecha: t.fecha || "23:00"
                  }));
                } else {
                  turnos = [{
                    abre: found.abre || "08:00",
                    fecha: found.fecha || "23:00"
                  }];
                }
                return { dia: k, aberto: !!found.aberto, turnos };
              });
              setHorarios(ordenado);
            }
          } catch (e) { console.warn("horarios_json inválido", e); }
        }
      }
    } catch (e) { console.error(e); }
  }, [lojaId]);

  useEffect(() => { fetchLoja(); }, [fetchLoja]);

  // ── Carregar config de pagamento da loja ──
  const PAG_API = "https://mixupbr.com.br/api/pagamentos";
  const _pagHeaders = (ct = true) => {
    const t = localStorage.getItem("mixup_loja_token");
    const h = {};
    if (ct) h["Content-Type"] = "application/json";
    if (t) h["Authorization"] = "Bearer " + t;
    return h;
  };
  const fetchPagConfig = useCallback(async () => {
    try {
      const r = await fetch(`${PAG_API}/?mod=config_loja&loja_id=${lojaId}`, { headers: _pagHeaders(false) });
      const d = await r.json();
      if (d.ok && d.dados) {
        const cfg = d.dados;
        setFApi(prev => ({
          ...prev,
          mp_access_token: cfg.mp_access_token || "",
          mp_public_key: cfg.mp_public_key || "",
          usar_chave_propria: cfg.usar_chave_propria ? 1 : 0,
          token_configurado: cfg.token_configurado ? 1 : 0,
        }));
        setPagOnlineAtivo(!!cfg.ativo);
        setTiposPag(prev => prev.map(t => {
          if (t.key === "pix") return { ...t, ativo: cfg.aceita_pix !== undefined ? !!Number(cfg.aceita_pix) : t.ativo };
          if (t.key === "credito") return { ...t, ativo: cfg.aceita_credito !== undefined ? !!Number(cfg.aceita_credito) : t.ativo };
          if (t.key === "debito") return { ...t, ativo: cfg.aceita_debito !== undefined ? !!Number(cfg.aceita_debito) : t.ativo };
          return t;
        }));
      }
    } catch (e) { console.warn("pagConfig fetch:", e); }
  }, [lojaId]);
  useEffect(() => { fetchPagConfig(); }, [fetchPagConfig]);

  const salvarPagConfig = async () => {
    setSaving(true);
    try {
      const pix = tiposPag.find(t => t.key === "pix");
      const credito = tiposPag.find(t => t.key === "credito");
      const debito = tiposPag.find(t => t.key === "debito");
      const body = {
        loja_id: lojaId,
        usar_chave_propria: fApi.usar_chave_propria ? 1 : 0,
        mp_access_token: fApi.usar_chave_propria ? fApi.mp_access_token : null,
        mp_public_key: fApi.usar_chave_propria ? fApi.mp_public_key : null,
        aceita_pix: pix?.ativo ? 1 : 0,
        aceita_credito: credito?.ativo ? 1 : 0,
        aceita_debito: debito?.ativo ? 1 : 0,
        ativo: pagOnlineAtivo ? 1 : 0,
      };
      const r = await fetch(`${PAG_API}/?mod=config_loja`, {
        method: "POST", headers: _pagHeaders(true),
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.ok) { notify(_("apiConfigurada")); fetchPagConfig(); }
      else { notify(d.error || "Erro ao salvar", false); }
    } catch (e) { notify("Erro: " + e.message, false); }
    setSaving(false);
  };

  const salvarDados = async () => {
    setSaving(true);
    try { await put("lojas", lojaId, fDados); notify(_("dadosAtualizados")); fetchLoja(); }
    catch (e) { notify(_("erroSalvar"), false); }
    finally { setSaving(false); }
  };

  const iS = { width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, fontFamily: "inherit", outline: "none" };
  const lS = { fontSize: 12, fontWeight: 600, color: C.textSoft, marginBottom: 6, display: "block" };
  const bS = { padding: 14, borderRadius: 12, border: "none", color: "#fff", fontWeight: 700, fontSize: 15, background: C.green, marginTop: 12, cursor: "pointer", width: "100%" };

  // ── CONFIGURAÇÃO DE TELA ──────────────────────────────────────────
  const CORES_ACCENT = [
    { cor: "#E8201A", nome: "Vermelho" },
    { cor: "#3B82F6", nome: "Azul" },
    { cor: "#8B5CF6", nome: "Roxo" },
    { cor: "#22C55E", nome: "Verde" },
    { cor: "#F97316", nome: "Laranja" },
    { cor: "#EC4899", nome: "Rosa" },
    { cor: "#06B6D4", nome: "Ciano" },
    { cor: "#FACC15", nome: "Amarelo" },
  ];

  const TEMAS = [
    { key: "noite", label: "Noite", icon: "🌙", desc: "Escuro profundo — padrão" },
    { key: "dia", label: "Dia", icon: "☀️", desc: "Claro e limpo" },
    { key: "escuro", label: "Ultra Escuro", icon: "⬛", desc: "Preto puro" },
    { key: "grafite", label: "Grafite", icon: "🔵", desc: "Azul escuro moderno" },
  ];

  const FONTES = [
    { key: "pequeno", label: "Pequeno", size: "12px" },
    { key: "normal", label: "Normal", size: "14px" },
    { key: "grande", label: "Grande", size: "16px" },
    { key: "extragrande", label: "Extra Grande", size: "18px" },
  ];

  const ConfigTela = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* MODO DIA / NOITE */}
      <div style={{ background: C.card, borderRadius: 16, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Modo de Exibição</h3>
          <p style={{ fontSize: 13, color: C.textMuted }}>Escolha o tema visual do painel</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {TEMAS.map(t => {
            const active = themeCfg.preset === t.key;
            const prev = PRESETS[t.key];
            return (
              <div key={t.key} onClick={() => updateConfig({ preset: t.key })} style={{
                padding: "16px 18px", borderRadius: 14, cursor: "pointer", transition: "all .2s",
                border: active ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                background: active ? `${C.red}12` : C.bg,
                display: "flex", alignItems: "center", gap: 14,
              }}>
                {/* mini preview do tema */}
                <div style={{ width: 44, height: 44, borderRadius: 10, background: prev.bg, border: `2px solid ${prev.border}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                  <div style={{ position: "absolute", left: 0, top: 0, width: 14, height: "100%", background: prev.sidebar }} />
                  <div style={{ marginLeft: 6, width: 22, height: 6, borderRadius: 3, background: prev.red }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: active ? C.text : C.textSoft, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>{t.icon}</span> {t.label}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{t.desc}</div>
                </div>
                {active && (
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", fontWeight: 800 }}>✓</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* COR DE DESTAQUE */}
      <div style={{ background: C.card, borderRadius: 16, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Cor de Destaque</h3>
          <p style={{ fontSize: 13, color: C.textMuted }}>Cor principal dos botões, badges e elementos ativos</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          {CORES_ACCENT.map(({ cor, nome }) => {
            const active = themeCfg.accentColor === cor;
            return (
              <div key={cor} onClick={() => updateConfig({ accentColor: cor })} title={nome} style={{
                width: 44, height: 44, borderRadius: 12, background: cor, cursor: "pointer", transition: "all .2s",
                border: active ? `3px solid ${C.text}` : "3px solid transparent",
                boxShadow: active ? `0 0 0 2px ${cor}` : "none",
                transform: active ? "scale(1.15)" : "scale(1)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {active && <span style={{ fontSize: 16, color: "#fff", fontWeight: 800, textShadow: "0 1px 3px rgba(0,0,0,.5)" }}>✓</span>}
              </div>
            );
          })}
        </div>
        {/* cor customizada */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.textSoft, flex: 1 }}>Cor personalizada</label>
          <input type="color" value={themeCfg.accentColor} onChange={e => updateConfig({ accentColor: e.target.value })}
            style={{ width: 44, height: 36, borderRadius: 8, border: "none", cursor: "pointer", background: "transparent" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, fontFamily: "monospace" }}>{themeCfg.accentColor}</span>
        </div>
      </div>

      {/* TAMANHO DA FONTE */}
      <div style={{ background: C.card, borderRadius: 16, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Tamanho da Fonte</h3>
          <p style={{ fontSize: 13, color: C.textMuted }}>Ajuste o tamanho do texto em todo o painel</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {FONTES.map(f => {
            const active = themeCfg.fontSize === f.key;
            return (
              <div key={f.key} onClick={() => updateConfig({ fontSize: f.key })} style={{
                flex: 1, padding: "16px 12px", borderRadius: 12, cursor: "pointer", textAlign: "center", transition: "all .2s",
                border: active ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                background: active ? `${C.red}12` : C.bg,
              }}>
                <div style={{ fontSize: f.size, fontWeight: 700, color: active ? C.text : C.textSoft, marginBottom: 4 }}>Aa</div>
                <div style={{ fontSize: 11, color: active ? C.red : C.textMuted, fontWeight: 600 }}>{f.label}</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{f.size}</div>
              </div>
            );
          })}
        </div>
        {/* preview de texto */}
        <div style={{ marginTop: 16, padding: "14px 16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>Preview</div>
          <div style={{ fontSize: ({ pequeno: 12, normal: 14, grande: 16, extragrande: 18 }[themeCfg.fontSize] || 14), color: C.text, fontWeight: 500 }}>
            MixUp — Painel do Lojista
          </div>
          <div style={{ fontSize: ({ pequeno: 10, normal: 12, grande: 13, extragrande: 15 }[themeCfg.fontSize] || 12), color: C.textMuted, marginTop: 4 }}>
            Pedidos, relatórios e configurações em um só lugar
          </div>
        </div>
      </div>

      {/* BOTÃO RESETAR */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => { updateConfig({ preset: "noite", accentColor: "#E8201A", fontSize: "normal" }); notify("Configurações resetadas!"); }}
          style={{ padding: "10px 22px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          Resetar padrão
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ animation: "fadeUp .4s ease-out" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 800, fontSize: 28 }}>{_("configuracao")}</h1>
      </div>

      {toast && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, background: toast.ok ? C.green : C.red, color: "#fff", padding: "12px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14, animation: "slideDown .3s ease-out" }}>{toast.msg}</div>}

      {/* CONFIGURAÇÃO DE TELA */}
      {sub === "configuracao_tela" && <ConfigTela />}

      {/* HORÁRIOS */}
      {sub === "horarios" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{_("horariosFuncionamento")}</h3>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>
            {_("horariosTurnoDica") || "Adicione vários turnos no mesmo dia (ex: café da manhã, almoço, jantar)."}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {horarios.map((h, i) => (
              <div key={i} style={{ padding: "14px 16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
                {/* Cabeçalho do dia: nome + toggle aberto/fechado */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: h.aberto ? 12 : 0 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, width: 90 }}>{labelDia(h.dia)}</span>
                  <div onClick={() => { const n = [...horarios]; n[i].aberto = !n[i].aberto; setHorarios(n); }}
                    style={{ width: 44, height: 24, borderRadius: 12, background: h.aberto ? C.green : C.border, cursor: "pointer", position: "relative", transition: "all .2s" }}>
                    <div style={{ width: 20, height: 20, borderRadius: 10, background: "#fff", position: "absolute", top: 2, left: h.aberto ? 22 : 2, transition: "all .2s" }} />
                  </div>
                  <span style={{ fontSize: 12, color: h.aberto ? C.greenLight : C.textMuted, fontWeight: 600 }}>
                    {h.aberto ? _("aberto") : _("fechado")}
                  </span>
                </div>

                {/* Turnos do dia (se aberto) */}
                {h.aberto && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 106 }}>
                    {h.turnos.map((t, ti) => (
                      <div key={ti} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input type="time" value={t.abre}
                          onChange={e => {
                            const n = [...horarios];
                            n[i].turnos[ti].abre = e.target.value;
                            setHorarios(n);
                          }}
                          style={{ ...iS, width: 130 }} />
                        <span style={{ color: C.textMuted }}>—</span>
                        <input type="time" value={t.fecha}
                          onChange={e => {
                            const n = [...horarios];
                            n[i].turnos[ti].fecha = e.target.value;
                            setHorarios(n);
                          }}
                          style={{ ...iS, width: 130 }} />
                        {h.turnos.length > 1 && (
                          <button
                            onClick={() => {
                              const n = [...horarios];
                              n[i].turnos.splice(ti, 1);
                              setHorarios(n);
                            }}
                            title={_("removerTurno") || "Remover turno"}
                            style={{
                              width: 28, height: 28, borderRadius: 6,
                              background: "transparent", border: `1px solid ${C.border}`,
                              color: C.red, cursor: "pointer", fontSize: 16, lineHeight: 1
                            }}>×</button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const n = [...horarios];
                        // Sugere próximo turno baseado no fim do anterior
                        const ultimo = n[i].turnos[n[i].turnos.length - 1];
                        const sugAbre = ultimo?.fecha || "14:00";
                        n[i].turnos.push({ abre: sugAbre, fecha: "23:00" });
                        setHorarios(n);
                      }}
                      style={{
                        alignSelf: "flex-start", padding: "6px 12px",
                        background: "transparent", border: `1px dashed ${C.border}`,
                        borderRadius: 6, color: C.textMuted, cursor: "pointer",
                        fontSize: 12, fontWeight: 600
                      }}>
                      + {_("adicionarTurno") || "Adicionar turno"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button className="form-btn" onClick={async () => {
            if (saving) return;
            setSaving(true);
            try {
              await put("lojas", lojaId, { horarios_json: JSON.stringify(horarios) });
              notify(_("horariosAlvo"));
              fetchLoja();
            } catch (e) {
              notify(_("erroSalvar") || "Erro ao salvar", false);
            }
            setSaving(false);
          }} style={bS} disabled={saving}>{saving ? "..." : _("salvarHorarios")}</button>
        </Card>
      )}

      {/* DADOS DA CONTA */}
      {sub === "dados_conta" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Card>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{_("informacoesLoja")}</h3>
            <Field iS={iS} lS={lS} label={`${_("nomeLoja")} *`} ph="Nome" value={fDados.nome} onChange={v => setFDados({ ...fDados, nome: v })} />
            <Field iS={iS} lS={lS} label="CNPJ" ph="00.000.000/0001-00" value={fDados.cnpj} onChange={v => setFDados({ ...fDados, cnpj: v })} />
            <Field iS={iS} lS={lS} label={_("telefone")} ph="(67) 99999-9999" value={fDados.telefone} onChange={v => setFDados({ ...fDados, telefone: v })} />
            <Field iS={iS} lS={lS} label={_("email")} ph="loja@email.com" value={fDados.email} onChange={v => setFDados({ ...fDados, email: v })} />
            <Field iS={iS} lS={lS} label={_("endereco")} ph="Rua, número" value={fDados.endereco} onChange={v => setFDados({ ...fDados, endereco: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field iS={iS} lS={lS} label={_("cidade")} ph="Ponta Porã" value={fDados.cidade} onChange={v => setFDados({ ...fDados, cidade: v })} />
              <Field iS={iS} lS={lS} label={_("estado")} ph="MS" value={fDados.estado} onChange={v => setFDados({ ...fDados, estado: v })} />
            </div>
            <div style={{ marginTop: 4 }}>
              <Field iS={iS} lS={lS} label="Cupom / Promoção" ph='Ex: R$ 10 OFF, Frete grátis, 20% desconto' value={fDados.cupom} onChange={v => setFDados({ ...fDados, cupom: v })} />
              <div style={{ fontSize: 10, color: C.textMuted, marginTop: -8, marginBottom: 8 }}>Aparece como tag verde no app do cliente. Deixe vazio para não mostrar.</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field iS={iS} lS={lS} label="Latitude" ph="-22.5339" value={fDados.latitude} onChange={v => setFDados({ ...fDados, latitude: v })} />
              <Field iS={iS} lS={lS} label="Longitude" ph="-55.7257" value={fDados.longitude} onChange={v => setFDados({ ...fDados, longitude: v })} />
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: -8, marginBottom: 4 }}>Coordenadas da loja — necessário para "Próximos de você" no app. Pegue no Google Maps.</div>
            <button className="form-btn" onClick={salvarDados} style={{ ...bS, opacity: saving ? .6 : 1 }} disabled={saving}>{saving ? _("salvando") : _("salvarDados")}</button>
          </Card>
          <Card>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{_("informacoes")}</h3>
            <div style={{ padding: "16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>{_("idLoja")}</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>#{lojaId}</div>
            </div>
            <div style={{ padding: "14px 16px", background: `${C.blue}10`, borderRadius: 12, border: `1px solid ${C.blue}30` }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>{_("taxasAdmin")}</div>
            </div>
          </Card>
        </div>
      )}

      {/* API PAGAMENTO */}
      {sub === "api_pagamento" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Card>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{_("integracaoMP")}</h3>
            <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>{_("configCredenciais")}</p>

            {/* Toggle pagamento online ativo */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Pagamento Online</span>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Aceitar pagamentos via app (Pix, cartão)</div>
              </div>
              <div onClick={() => setPagOnlineAtivo(prev => !prev)}
                style={{ width: 50, height: 28, borderRadius: 14, background: pagOnlineAtivo ? C.green : C.border, cursor: "pointer", position: "relative", transition: "all .2s" }}>
                <div style={{ width: 24, height: 24, borderRadius: 12, background: "#fff", position: "absolute", top: 2, left: pagOnlineAtivo ? 24 : 2, transition: "all .2s" }} />
              </div>
            </div>

            {/* Toggle usar chave própria */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 14 }}>Usar minha própria chave Mercado Pago</span>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                  {fApi.usar_chave_propria ? "O dinheiro cai direto na SUA conta" : "O dinheiro cai na conta do MixUp (plataforma)"}
                </div>
              </div>
              <div onClick={() => setFApi(prev => ({ ...prev, usar_chave_propria: prev.usar_chave_propria ? 0 : 1 }))}
                style={{ width: 50, height: 28, borderRadius: 14, background: fApi.usar_chave_propria ? "#AB47BC" : C.border, cursor: "pointer", position: "relative", transition: "all .2s" }}>
                <div style={{ width: 24, height: 24, borderRadius: 12, background: "#fff", position: "absolute", top: 2, left: fApi.usar_chave_propria ? 24 : 2, transition: "all .2s" }} />
              </div>
            </div>

            {/* Campos MP — só se usar chave própria */}
            {fApi.usar_chave_propria ? <>
              <Field iS={iS} lS={lS} label={_("accessToken")} ph="APP_USR-..." value={fApi.mp_access_token} onChange={v => setFApi({ ...fApi, mp_access_token: v })} />
              <Field iS={iS} lS={lS} label={_("publicKey")} ph="APP_USR-..." value={fApi.mp_public_key} onChange={v => setFApi({ ...fApi, mp_public_key: v })} />
              <div style={{ padding: "14px 16px", background: fApi.token_configurado ? `${C.green}15` : `${C.red}10`, borderRadius: 12, border: `1px solid ${fApi.token_configurado ? C.green + "40" : C.red + "40"}`, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: fApi.token_configurado ? C.green : C.red }}>
                  {fApi.token_configurado ? "✅ Token configurado — pagamentos ativos" : "⚠️ Insira seu Access Token para ativar pagamentos"}
                </div>
              </div>
            </> : (
              <div style={{ padding: "14px 16px", background: `${C.blue}10`, borderRadius: 12, border: `1px solid ${C.blue}30`, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>
                  💡 Usando a chave da plataforma MixUp — o dinheiro dos pedidos online será repassado a você pela plataforma.
                </div>
              </div>
            )}

            <button className="form-btn" onClick={salvarPagConfig} style={{ ...bS, opacity: saving ? .6 : 1 }} disabled={saving}>
              {saving ? _("salvando") : _("salvarConfiguracao")}
            </button>
          </Card>
        </div>
      )}

      {/* IMPRESSORA */}
      {sub === "impressora" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{_("configImpressora")}</h3>

          {isPDVDesktop ? (
            <>
              {/* ═══ MODO PDV DESKTOP — config multi-destino ═══ */}
              <div style={{ padding: 10, background: "#22c55e15", border: "1px solid #22c55e55", borderRadius: 8, marginBottom: 16, fontSize: 12, color: C.text }}>
                <b style={{ color: "#22c55e" }}>✓ Modo PDV Desktop ativo</b><br/>
                Cada destino abaixo pode ter sua própria impressora (USB ou Rede TCP).
              </div>

              {/* Tabs de destinos */}
              <div style={{ display: "flex", gap: 6, marginBottom: 16, padding: 4, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                {[
                  { k: "caixa", l: "💰 Caixa", desc: "Recibo cliente" },
                  { k: "cozinha", l: "🍳 Cozinha", desc: "Comanda" },
                  { k: "bar", l: "🍺 Bar/Copa", desc: "Bebidas" }
                ].map(d => {
                  const ativo = pdvDestinoAtivo === d.k;
                  const cfgD = pdvPrinters[d.k];
                  return (
                    <button key={d.k} onClick={() => setPdvDestinoAtivo(d.k)}
                      style={{ flex: 1, padding: "10px 12px", borderRadius: 6, border: "none",
                        background: ativo ? C.red : "transparent", color: ativo ? "#fff" : C.text,
                        fontWeight: 700, fontSize: 13, cursor: "pointer", textAlign: "center",
                        position: "relative" }}>
                      {cfgD?.ativa && <span style={{ position: "absolute", top: 4, right: 6, width: 8, height: 8, background: "#22c55e", borderRadius: "50%" }}/>}
                      {d.l}
                      <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{d.desc}</div>
                    </button>
                  );
                })}
              </div>

              {/* Conteúdo do destino selecionado */}
              {(() => {
                const destino = pdvDestinoAtivo;
                const cfg = pdvPrinters[destino];
                const setCfg = (novo) => setDestino(destino, novo);

                return (
                  <>
                    {/* Toggle ativa */}
                    <div style={{ padding: 12, marginBottom: 14, background: cfg.ativa ? "#22c55e15" : `${C.border}30`, border: `1px solid ${cfg.ativa ? "#22c55e55" : C.border}`, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Impressora ATIVA neste destino</div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                          {cfg.ativa ? "Vai imprimir quando chegar pedido" : "Não vai imprimir aqui"}
                        </div>
                      </div>
                      <button onClick={() => setCfg({ ativa: !cfg.ativa })}
                        style={{ padding: "6px 14px", borderRadius: 6, border: "none",
                          background: cfg.ativa ? "#22c55e" : C.textMuted, color: "#fff",
                          fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                        {cfg.ativa ? "ATIVA" : "INATIVA"}
                      </button>
                    </div>

                    {cfg.ativa && (
                      <>
                        {/* Tipo de conexão */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={lS}>Tipo de Conexão</label>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={() => setCfg({ conexao: "printer" })}
                              style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: `2px solid ${cfg.conexao === "printer" ? C.red : C.border}`, background: cfg.conexao === "printer" ? `${C.red}15` : C.card, color: cfg.conexao === "printer" ? C.red : C.text, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                              USB / Windows
                            </button>
                            <button onClick={() => setCfg({ conexao: "tcp" })}
                              style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: `2px solid ${cfg.conexao === "tcp" ? C.red : C.border}`, background: cfg.conexao === "tcp" ? `${C.red}15` : C.card, color: cfg.conexao === "tcp" ? C.red : C.text, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                              Rede (Wi-Fi/Ethernet)
                            </button>
                          </div>
                        </div>

                        {/* USB - lista impressoras */}
                        {cfg.conexao === "printer" && (
                          <div style={{ marginBottom: 14 }}>
                            <label style={lS}>Impressora instalada no Windows</label>
                            <div style={{ maxHeight: 180, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 8, padding: 6 }}>
                              {pdvImpressoras.length === 0 && (
                                <div style={{ padding: 12, textAlign: "center", color: C.textMuted, fontSize: 12 }}>Nenhuma impressora encontrada</div>
                              )}
                              {pdvImpressoras.map((p, i) => {
                                const sel = cfg.name === p.nome;
                                return (
                                  <div key={i}
                                    onClick={() => {
                                      const novoTipo = (p.nome || "").toLowerCase().includes("star") ? "star"
                                        : (p.nome || "").toLowerCase().includes("tanca") ? "tanca"
                                        : (p.nome || "").toLowerCase().includes("daruma") ? "daruma"
                                        : "epson";
                                      setCfg({ name: p.nome, type: novoTipo });
                                    }}
                                    style={{ padding: 10, marginBottom: 4, borderRadius: 6, border: `2px solid ${sel ? C.red : (p.padrao ? "#22c55e55" : C.border)}`, background: sel ? `${C.red}15` : C.card, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div>
                                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                        {p.nome}
                                        {p.padrao && <span style={{ marginLeft: 6, padding: "1px 6px", background: "#22c55e", color: "#fff", borderRadius: 3, fontSize: 9, fontWeight: 700 }}>PADRÃO</span>}
                                      </div>
                                      {p.porta && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{p.porta}</div>}
                                    </div>
                                    {sel && <span style={{ fontSize: 18, color: C.red }}>✓</span>}
                                  </div>
                                );
                              })}
                            </div>
                            <button onClick={pdvAtualizarLista} disabled={pdvLoading} style={{ marginTop: 8, width: "100%", padding: 8, borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.text, cursor: pdvLoading ? "wait" : "pointer", fontSize: 12, fontWeight: 600 }}>
                              {pdvLoading ? "Atualizando..." : "🔄 Atualizar lista"}
                            </button>
                          </div>
                        )}

                        {/* TCP - IP e porta */}
                        {cfg.conexao === "tcp" && (
                          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 14 }}>
                            <div>
                              <label style={lS}>IP da Impressora</label>
                              <input value={cfg.ip} onChange={e => setCfg({ ip: e.target.value })} placeholder="192.168.1.100" style={iS}/>
                            </div>
                            <div>
                              <label style={lS}>Porta</label>
                              <input type="number" value={cfg.port} onChange={e => setCfg({ port: parseInt(e.target.value) || 9100 })} placeholder="9100" style={iS}/>
                            </div>
                          </div>
                        )}

                        {/* Marca + Largura + Fonte */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                          <div>
                            <label style={lS}>Marca / Tipo</label>
                            <select value={cfg.type} onChange={e => setCfg({ type: e.target.value })} style={iS}>
                              <option value="epson">Epson / Elgin</option>
                              <option value="star">Star</option>
                              <option value="tanca">Tanca</option>
                              <option value="daruma">Daruma</option>
                            </select>
                          </div>
                          <div>
                            <label style={lS}>Largura (colunas)</label>
                            <select value={cfg.width} onChange={e => setCfg({ width: parseInt(e.target.value) })} style={iS}>
                              {[28,30,32,36,40,42,44,46,48,50,52,56,60,64,68,72].map(n => (
                                <option key={n} value={n}>{n} col</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={lS}>Fonte ESC/POS</label>
                            <select value={cfg.font} onChange={e => setCfg({ font: parseInt(e.target.value) })} style={iS}>
                              <option value="0">A — Normal</option>
                              <option value="1">B — Pequena</option>
                              <option value="2">Condensada</option>
                              <option value="3">Dupla altura</option>
                              <option value="4">Dupla largura</option>
                            </select>
                          </div>
                        </div>

                        {/* Vias */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={lS}>Vias por pedido</label>
                          <select value={cfg.vias} onChange={e => setCfg({ vias: parseInt(e.target.value) })} style={iS}>
                            <option value="1">1 via</option>
                            <option value="2">2 vias</option>
                            <option value="3">3 vias</option>
                            <option value="4">4 vias</option>
                            <option value="5">5 vias</option>
                          </select>
                        </div>

                        {/* PRÉVIA */}
                        <div style={{ marginBottom: 14 }}>
                          <label style={lS}>Pré-visualização ({destino} • {cfg.width} colunas)</label>
                          <pre style={{
                            background: "#0a0a0a", color: "#22c55e",
                            border: `1px solid ${C.border}`, borderRadius: 8,
                            padding: 12, fontSize: 11, fontFamily: "'Courier New', Courier, monospace",
                            overflow: "auto", maxHeight: 240, margin: 0,
                            whiteSpace: "pre", lineHeight: 1.4
                          }}>{gerarPreview(destino)}</pre>
                        </div>

                        {/* Auto cut */}
                        <div style={{ marginBottom: 16 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text }}>
                            <input type="checkbox" checked={cfg.auto_cut} onChange={e => setCfg({ auto_cut: e.target.checked })}/>
                            Cortar papel automaticamente
                          </label>
                        </div>

                        {/* Botão testar destino */}
                        <button onClick={() => pdvTestar(destino)} disabled={pdvTestando}
                          style={{ width: "100%", padding: 12, borderRadius: 8, border: "none",
                            background: "#22c55e", color: "#fff", fontWeight: 700, fontSize: 14,
                            cursor: pdvTestando ? "wait" : "pointer", marginBottom: 10 }}>
                          {pdvTestando ? "Testando..." : `🖨 Testar Impressão (${destino})`}
                        </button>
                      </>
                    )}
                  </>
                );
              })()}

              {/* Auto-print global */}
              <div style={{ padding: 12, marginTop: 16, background: `${C.border}20`, borderRadius: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text }}>
                  <input type="checkbox" checked={pdvAutoPrint} onChange={e => setPdvAutoPrint(e.target.checked)}/>
                  <b>Imprimir automaticamente</b> ao receber pedido (em todos os destinos ativos)
                </label>
              </div>

              {/* Botão salvar global */}
              <button onClick={async () => { const ok = await pdvSalvar(); notify(ok ? "Configuração salva!" : "Erro ao salvar", ok); }}
                className="form-btn"
                style={{ ...bS, width: "100%", marginTop: 16 }}>
                💾 Salvar Todas as Configurações
              </button>
            </>
          ) : (
            <>
              {/* ═══ MODO NAVEGADOR (legado) ═══ */}
              <div style={{ padding: 10, background: "#f9731615", border: "1px solid #f9731655", borderRadius: 8, marginBottom: 16, fontSize: 12, color: C.text }}>
                <b style={{ color: "#f97316" }}>⚠ Modo Navegador</b><br/>
                Para impressão silenciosa profissional, abra este painel pelo <b>MixUp PDV Desktop</b>.
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lS}>{_("tipoConexao")}</label>
                <select value={fImp.tipo} onChange={e => setFImp({ ...fImp, tipo: e.target.value })} style={iS}>
                  <option value="bluetooth">{_("bluetooth")}</option>
                  <option value="usb">{_("usb")}</option>
                  <option value="wifi">{_("wifiRede")}</option>
                </select>
              </div>
              <Field iS={iS} lS={lS} label={_("nomeDispositivo")} ph={fImp.tipo === "wifi" ? "192.168.1.100" : "Impressora-BT"} value={fImp.nome_dispositivo} onChange={v => setFImp({ ...fImp, nome_dispositivo: v })} />
              <div style={{ marginBottom: 14 }}>
                <label style={lS}>{_("larguraPapel")}</label>
                <select value={fImp.largura_papel} onChange={e => setFImp({ ...fImp, largura_papel: e.target.value })} style={iS}>
                  <option value="58">58mm</option><option value="80">80mm</option>
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lS}>{_("imprimirAuto")}</label>
                <select value={fImp.auto_imprimir} onChange={e => setFImp({ ...fImp, auto_imprimir: e.target.value })} style={iS}>
                  <option value="1">{_("simImprimir")}</option><option value="0">{_("naoImprimir")}</option>
                </select>
              </div>
              <button className="form-btn" onClick={() => notify(_("impressoraConfigurada"))} style={bS}>{_("salvarImpressora")}</button>
            </>
          )}
        </Card>
      )}

      {/* TIPOS DE PAGAMENTO */}
      {sub === "tipos_pagamento" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{_("tiposPagAceitos")}</h3>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>{_("ativeDesative")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tiposPag.map((t, i) => (
              <div key={t.key} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
                <span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{t.label}</span>
                <div onClick={() => { const n = [...tiposPag]; n[i].ativo = !n[i].ativo; setTiposPag(n); }}
                  style={{ width: 50, height: 28, borderRadius: 14, background: t.ativo ? C.green : C.border, cursor: "pointer", position: "relative", transition: "all .2s" }}>
                  <div style={{ width: 24, height: 24, borderRadius: 12, background: "#fff", position: "absolute", top: 2, left: t.ativo ? 24 : 2, transition: "all .2s", boxShadow: "0 2px 4px rgba(0,0,0,.2)" }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: t.ativo ? C.greenLight : C.textMuted, width: 50 }}>{t.ativo ? _("ativo") : _("inativo")}</span>
              </div>
            ))}
          </div>
          <button className="form-btn" onClick={salvarPagConfig} style={bS}>{_("salvarPagamentos")}</button>
        </Card>
      )}

      {/* IDIOMAS */}
      {sub === "idiomas" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{_("idiomasTitulo")}</h3>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 24 }}>{_("idiomasDesc")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {LANGUAGES.map(l => (
              <div key={l.key} onClick={() => { setLang(l.key); notify(_("idiomaSalvo")); }}
                style={{
                  display: "flex", alignItems: "center", gap: 16, padding: "18px 22px",
                  background: lang === l.key ? `${C.red}12` : C.bg,
                  borderRadius: 14,
                  border: lang === l.key ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                  cursor: "pointer", transition: "all .2s",
                }}>
                <span style={{ fontSize: 28 }}>{l.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: lang === l.key ? C.text : C.textSoft }}>{l.label}</div>
                </div>
                {lang === l.key && (
                  <div style={{ padding: "4px 14px", borderRadius: 8, background: C.red, color: "#fff", fontSize: 12, fontWeight: 700 }}>
                    {_("idiomaAtual")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
